import {
    isServiceType,
    SERVICE_KIND_BY_TYPE,
    SERVICE_KINDS,
    TNormalizedService,
    TServiceAdditionalDetails,
    TServiceBilling,
    TServiceDetail,
    TServiceGraphHistory,
    TServiceGraphMeta,
    TServiceKind,
    TServiceUsageData,
    TServiceUsageMetric,
} from '../../types/serviceView'
import { normalizeService } from './normalize'
import { asString, isRecord, TUnknownRecord } from './common'

const SECTION_KIND_BY_ID: Record<string, TServiceKind> = {
    'cloud-connect': SERVICE_KINDS.CONNECTIVITY,
    'cloud-direct': SERVICE_KINDS.CONNECTIVITY,
    'dc-connect': SERVICE_KINDS.CONNECTIVITY,
    'dc-port': SERVICE_KINDS.CONNECTIVITY,
    'internet': SERVICE_KINDS.CONNECTIVITY,
    'ip-transit': SERVICE_KINDS.CONNECTIVITY,
    'broadband': SERVICE_KINDS.BROADBAND,
    'dns-service': SERVICE_KINDS.DNS,
    'veeam-backup': SERVICE_KINDS.VEEAM,
    'cloud-backup-replication': SERVICE_KINDS.VEEAM,
}

const humanize = (value: string): string =>
    value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (character) => character.toUpperCase())

const toNumber = (value: unknown): number | undefined => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : undefined
}

const normalizeRateUnit = (value: unknown): string | undefined => {
    const stringValue = asString(value).trim()

    if (!stringValue) {
        return undefined
    }

    return stringValue.replace(/\bmbs\b/gi, 'Mbps')
}

const normalizeGraphMeta = (
    rawGraph: unknown,
    index: number
): TServiceGraphMeta | null => {
    if (typeof rawGraph === 'string') {
        return {
            id: rawGraph,
            label: rawGraph,
        }
    }

    if (!isRecord(rawGraph)) {
        return null
    }

    const id =
        asString(rawGraph.id || rawGraph.graphId || rawGraph.key) ||
        `graph-${index + 1}`
    const label = asString(
        rawGraph.name || rawGraph.label || rawGraph.title || id
    )

    return {
        id,
        label,
        ...(rawGraph.type ? { type: asString(rawGraph.type) } : {}),
        ...(rawGraph.unit ? { unit: asString(rawGraph.unit) } : {}),
    }
}

const normalizeBilling = (rawBilling: unknown): TServiceBilling => {
    if (!isRecord(rawBilling)) {
        return null
    }

    return {
        ...rawBilling,
        ...(rawBilling.install !== undefined
            ? { install: toNumber(rawBilling.install) }
            : {}),
        ...(rawBilling.rental !== undefined
            ? { rental: toNumber(rawBilling.rental) }
            : {}),
        ...(rawBilling.billedTo
            ? { billedTo: asString(rawBilling.billedTo) }
            : {}),
    }
}

export const hasRenderableBillingDetails = (
    billing: TServiceBilling
): boolean => {
    if (!billing) {
        return false
    }

    return Boolean(
        (typeof billing.install === 'number' && billing.install > 0) ||
            (typeof billing.rental === 'number' && billing.rental > 0) ||
            (typeof billing.billedTo === 'string' && billing.billedTo.trim())
    )
}

const normalizeAdditionalDetails = (
    rawAdditionalDetails: unknown
): TServiceAdditionalDetails => {
    if (Array.isArray(rawAdditionalDetails) && rawAdditionalDetails.length === 0) {
        return null
    }

    if (!isRecord(rawAdditionalDetails)) {
        return null
    }

    const ips = Array.isArray(rawAdditionalDetails.ips)
        ? rawAdditionalDetails.ips
              .map((value) => asString(value))
              .filter(Boolean)
        : undefined

    return {
        ...rawAdditionalDetails,
        ...(ips ? { ips } : {}),
        ...(rawAdditionalDetails.transferLimit !== undefined
            ? { transferLimit: toNumber(rawAdditionalDetails.transferLimit) }
            : {}),
        ...(rawAdditionalDetails.bearer
            ? { bearer: normalizeRateUnit(rawAdditionalDetails.bearer) }
            : {}),
        ...(rawAdditionalDetails.bandwidth
            ? { bandwidth: normalizeRateUnit(rawAdditionalDetails.bandwidth) }
            : {}),
        ...(rawAdditionalDetails.storageQuota !== undefined
            ? { storageQuota: toNumber(rawAdditionalDetails.storageQuota) }
            : {}),
        ...(rawAdditionalDetails.usedStorageQuota !== undefined
            ? {
                  usedStorageQuota: toNumber(
                      rawAdditionalDetails.usedStorageQuota
                  ),
              }
            : {}),
        ...(rawAdditionalDetails.vms !== undefined
            ? { vms: toNumber(rawAdditionalDetails.vms) }
            : {}),
        ...(rawAdditionalDetails.rentalPeriod !== undefined
            ? { rentalPeriod: toNumber(rawAdditionalDetails.rentalPeriod) }
            : {}),
    }
}

export const resolveServiceKindFromSectionId = (
    sectionId?: string
): TServiceKind | undefined => {
    if (!sectionId) {
        return undefined
    }

    return SECTION_KIND_BY_ID[sectionId]
}

export const resolveServiceKind = (
    type?: string,
    sectionId?: string
): TServiceKind => {
    if (type && isServiceType(type)) {
        return SERVICE_KIND_BY_TYPE[type]
    }

    return resolveServiceKindFromSectionId(sectionId) || SERVICE_KINDS.GENERIC
}

type TNormalizeServiceDetailOptions = {
    previousService?: Partial<TNormalizedService> | null
    sectionId?: string
    groupName?: string
}

export const normalizeServiceDetail = (
    rawService: unknown,
    options: TNormalizeServiceDetailOptions = {}
): TServiceDetail => {
    const raw = isRecord(rawService) ? rawService : {}
    const base = normalizeService(rawService, {
        groupName: options.groupName || options.previousService?.groupName,
    })
    const graphs = Array.isArray(raw.graphs)
        ? raw.graphs
              .map((graph, index) => normalizeGraphMeta(graph, index))
              .filter((graph): graph is TServiceGraphMeta => Boolean(graph))
        : []

    return {
        ...base,
        resolvedKind: resolveServiceKind(
            base.type,
            options.sectionId
        ),
        billing: normalizeBilling(raw.billing),
        additionalDetails: normalizeAdditionalDetails(raw.additionalDetails),
        graphs,
        raw: rawService,
    }
}

const createUsageMetric = (
    id: string,
    value: unknown,
    unit?: string
): TServiceUsageMetric | null => {
    if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '')
    ) {
        return null
    }

    return {
        id,
        label: humanize(id),
        value:
            typeof value === 'boolean'
                ? String(value)
                : typeof value === 'number' || typeof value === 'string'
                ? value
                : JSON.stringify(value),
        ...(unit ? { unit } : {}),
    }
}

const normalizeUsageMetricsFromObject = (
    rawUsage: TUnknownRecord
): TServiceUsageMetric[] => {
    const ignoredKeys = new Set(['id', 'product', 'month', 'year', 'day'])

    return Object.entries(rawUsage)
        .filter(
            ([key, value]) =>
                !ignoredKeys.has(key) &&
                (typeof value === 'number' ||
                    typeof value === 'string' ||
                    typeof value === 'boolean')
        )
        .map(([key, value]) => {
            const unit = ['download', 'upload'].includes(key)
                ? 'bytes'
                : undefined
            return createUsageMetric(key, value, unit)
        })
        .filter((metric): metric is TServiceUsageMetric => Boolean(metric))
}

export const normalizeServiceUsage = (rawUsage: unknown): TServiceUsageData => {
    if (rawUsage === null || rawUsage === undefined) {
        return { metrics: [], days: [], raw: rawUsage, empty: true }
    }

    if (Array.isArray(rawUsage)) {
        if (rawUsage.length === 0) {
            return { metrics: [], days: [], raw: rawUsage, empty: true }
        }

        const days = rawUsage
            .map((row) => {
                if (!isRecord(row)) {
                    return null
                }

                const day = toNumber(row.day)
                const download = toNumber(row.download) || 0
                const upload = toNumber(row.upload) || 0

                return day !== undefined
                    ? {
                          day,
                          download,
                          upload,
                      }
                    : null
            })
            .filter(
                (
                    day
                ): day is {
                    day: number
                    download: number
                    upload: number
                } => Boolean(day)
            )

        const totals = rawUsage.reduce<Record<string, number>>((carry, row) => {
            if (!isRecord(row)) {
                return carry
            }

            ;['download', 'upload'].forEach((key) => {
                const numericValue = toNumber(row[key])
                if (numericValue !== undefined) {
                    carry[key] = (carry[key] || 0) + numericValue
                }
            })

            return carry
        }, {})

        const metrics = Object.entries(totals)
            .map(([key, value]) => createUsageMetric(key, value, 'bytes'))
            .filter((metric): metric is TServiceUsageMetric => Boolean(metric))

        return {
            metrics,
            days,
            raw: rawUsage,
            empty: metrics.length === 0 && days.length === 0,
        }
    }

    if (isRecord(rawUsage)) {
        if (Array.isArray(rawUsage.usage)) {
            return normalizeServiceUsage(rawUsage.usage)
        }

        const metrics = normalizeUsageMetricsFromObject(rawUsage)
        return { metrics, days: [], raw: rawUsage, empty: metrics.length === 0 }
    }

    return { metrics: [], days: [], raw: rawUsage, empty: true }
}

const normalizeSeriesDataPoints = (rawSeries: unknown): number[][] => {
    if (Array.isArray(rawSeries)) {
        if (
            rawSeries.every((item) => Array.isArray(item) && item.length >= 2)
        ) {
            return rawSeries
                .map((item) => {
                    const timestamp = toNumber(item[0])
                    const value = toNumber(item[1])
                    return timestamp !== undefined && value !== undefined
                        ? [timestamp, value]
                        : null
                })
                .filter((item): item is number[] => Boolean(item))
        }

        return rawSeries
            .map((item) => {
                if (!isRecord(item)) {
                    return null
                }

                const timestamp = toNumber(
                    item.timestamp || item.time || item.clock || item.date
                )
                const value = toNumber(
                    item.value || item.in || item.out || item.y
                )

                return timestamp !== undefined && value !== undefined
                    ? [timestamp, value]
                    : null
            })
            .filter((item): item is number[] => Boolean(item))
    }

    if (isRecord(rawSeries) && Array.isArray(rawSeries.data)) {
        return normalizeSeriesDataPoints(rawSeries.data)
    }

    return []
}

const normalizeSeries = (rawSeries: unknown, fallbackLabel: string) => {
    if (isRecord(rawSeries)) {
        return {
            label: asString(rawSeries.label || rawSeries.name || fallbackLabel),
            data: normalizeSeriesDataPoints(
                rawSeries.data ||
                    rawSeries.points ||
                    rawSeries.series ||
                    rawSeries
            ),
        }
    }

    return {
        label: fallbackLabel,
        data: normalizeSeriesDataPoints(rawSeries),
    }
}

export const normalizeServiceGraphHistory = (
    rawGraph: unknown,
    graph: TServiceGraphMeta
): TServiceGraphHistory => {
    let dataIn = normalizeSeries([], 'Inbound')
    let dataOut = normalizeSeries([], 'Outbound')

    if (Array.isArray(rawGraph) && rawGraph.length >= 2) {
        dataIn = normalizeSeries(rawGraph[0], graph.label || 'Inbound')
        dataOut = normalizeSeries(rawGraph[1], 'Outbound')
    } else if (isRecord(rawGraph)) {
        dataIn = normalizeSeries(
            rawGraph.dataIn ||
                rawGraph.inbound ||
                rawGraph.input ||
                rawGraph.download ||
                rawGraph.in,
            graph.label || 'Inbound'
        )
        dataOut = normalizeSeries(
            rawGraph.dataOut ||
                rawGraph.outbound ||
                rawGraph.output ||
                rawGraph.upload ||
                rawGraph.out,
            'Outbound'
        )

        if (
            dataIn.data.length === 0 &&
            dataOut.data.length === 0 &&
            Array.isArray(rawGraph.series)
        ) {
            dataIn = normalizeSeries(
                rawGraph.series[0],
                graph.label || 'Inbound'
            )
            dataOut = normalizeSeries(rawGraph.series[1], 'Outbound')
        }
    }

    const rowMap = new Map<
        number,
        { timestamp: number; inbound?: number; outbound?: number }
    >()

    dataIn.data.forEach(([timestamp, value]) => {
        rowMap.set(timestamp, {
            ...(rowMap.get(timestamp) || { timestamp }),
            timestamp,
            inbound: value,
        })
    })

    dataOut.data.forEach(([timestamp, value]) => {
        rowMap.set(timestamp, {
            ...(rowMap.get(timestamp) || { timestamp }),
            timestamp,
            outbound: value,
        })
    })

    const rows = Array.from(rowMap.values()).sort(
        (left, right) => right.timestamp - left.timestamp
    )

    return {
        dataIn,
        dataOut,
        rows,
        empty: rows.length === 0,
    }
}

const isPrimitiveMetadataValue = (value: unknown): boolean =>
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'

export const flattenServiceMetadata = (
    data?: Record<string, unknown> | null
): Array<{ key: string; label: string; value: string }> => {
    if (!data) {
        return []
    }

    return Object.entries(data).flatMap(([key, value]) => {
        if (Array.isArray(value)) {
            const values = value
                .map((item) => asString(item).trim())
                .filter(Boolean)

            return values.length
                ? [
                      {
                          key,
                          label: humanize(key),
                          value: values.join(', '),
                      },
                  ]
                : []
        }

        if (!isPrimitiveMetadataValue(value)) {
            return []
        }

        return [{ key, label: humanize(key), value: asString(value) }]
    })
}
